/**
 * Audio Visualizer — Breathing circle that reacts to audio amplitude.
 * Ported from ADA V2's Visualizer component.
 */
import React, { useEffect, useRef } from 'react';

export default function AudioVisualizer({ isActive, intensity = 0, state = 'ARMED' }) {
  const canvasRef = useRef(null);
  const intensityRef = useRef(intensity);
  const stateRef = useRef(state);
  const isActiveRef = useRef(isActive);

  useEffect(() => { intensityRef.current = intensity; }, [intensity]);
  useEffect(() => { stateRef.current = state; }, [state]);
  useEffect(() => { isActiveRef.current = isActive; }, [isActive]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const w = canvas.width = 200;
    const h = canvas.height = 200;
    const cx = w / 2;
    const cy = h / 2;
    let animId;

    const STATE_COLORS = {
      ARMED: [0, 215, 255],
      LISTENING: [25, 242, 214],
      THINKING: [255, 176, 32],
      SPEAKING: [0, 215, 255],
      STANDBY: [124, 138, 166],
    };

    const draw = () => {
      const ctx = canvas.getContext('2d');
      const t = Date.now() / 1000;
      const currentState = stateRef.current;
      const currentIntensity = intensityRef.current;
      const active = isActiveRef.current;
      const [r, g, b] = STATE_COLORS[currentState] || STATE_COLORS.ARMED;

      const baseRadius = 60;
      const breath = Math.sin(t * (active ? 3 : 1.5)) * (active ? 8 : 4);
      const audioBoost = currentIntensity * 25;
      const radius = baseRadius + breath + audioBoost;

      ctx.clearRect(0, 0, w, h);

      // Outer glow
      const glowGrad = ctx.createRadialGradient(cx, cy, radius * 0.5, cx, cy, radius * 1.8);
      glowGrad.addColorStop(0, `rgba(${r},${g},${b},${0.08 + currentIntensity * 0.12})`);
      glowGrad.addColorStop(1, 'transparent');
      ctx.fillStyle = glowGrad;
      ctx.fillRect(0, 0, w, h);

      // Main circle
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(${r},${g},${b},${active ? 0.8 : 0.4})`;
      ctx.lineWidth = active ? 3 : 2;
      ctx.shadowBlur = active ? 25 : 12;
      ctx.shadowColor = `rgb(${r},${g},${b})`;
      ctx.stroke();
      ctx.shadowBlur = 0;

      // Inner circle
      ctx.beginPath();
      ctx.arc(cx, cy, radius * 0.6, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(${r},${g},${b},0.15)`;
      ctx.lineWidth = 1;
      ctx.stroke();

      // Center dot
      ctx.beginPath();
      ctx.arc(cx, cy, 3, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${r},${g},${b},0.8)`;
      ctx.fill();

      // Audio waveform ring (when active)
      if (active && currentIntensity > 0.01) {
        const numBars = 32;
        for (let i = 0; i < numBars; i++) {
          const angle = (i / numBars) * Math.PI * 2;
          const barHeight = 5 + currentIntensity * 20 * (0.5 + 0.5 * Math.sin(t * 5 + i * 0.5));
          const x1 = cx + Math.cos(angle) * (radius + 5);
          const y1 = cy + Math.sin(angle) * (radius + 5);
          const x2 = cx + Math.cos(angle) * (radius + 5 + barHeight);
          const y2 = cy + Math.sin(angle) * (radius + 5 + barHeight);

          ctx.beginPath();
          ctx.moveTo(x1, y1);
          ctx.lineTo(x2, y2);
          ctx.strokeStyle = `rgba(${r},${g},${b},${0.3 + currentIntensity * 0.5})`;
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      }

      animId = requestAnimationFrame(draw);
    };

    draw();
    return () => cancelAnimationFrame(animId);
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{ width: '200px', height: '200px' }}
      data-testid="audio-visualizer"
    />
  );
}
